/**
 * GlobalErrorHandler - Catches unhandled errors and displays them to the user
 * 
 * Note: This uses console.log/error directly in some places because error handling
 * must work even if the Logger service itself has an issue.
 */

import { ModalSystem } from './ModalSystem';
import { Logger } from './Logger';

const logger = Logger.scope('GlobalErrorHandler');

export class GlobalErrorHandler {
  private static isInitialized = false;

  /**
   * Initialize the global error handler
   */
  public static init(): void {
    if (this.isInitialized) return;
    this.isInitialized = true;

    // Handle uncaught JavaScript errors
    window.addEventListener('error', (event: ErrorEvent) => {
      // Ignore benign ResizeObserver errors - these are harmless and occur frequently
      // when ResizeObserver callbacks take longer than one animation frame
      if (event.message?.includes('ResizeObserver loop')) {
        event.preventDefault();
        return;
      }
      
      logger.error('Uncaught error:', event.error);
      // Delay slightly to ensure modal system is ready and event loop has cleared
      setTimeout(() => {
        try {
          this.showErrorModal(event.error, event.message, event.filename, event.lineno, event.colno);
        } catch (err) {
          // Use console.error as fallback if logger fails
          console.error('GlobalErrorHandler: Failed to show error modal:', err);
        }
      }, 10);
      event.preventDefault(); // Prevent default browser error handling
    });

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      logger.error('Unhandled promise rejection:', event.reason);
      const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
      // Delay slightly to ensure modal system is ready
      setTimeout(() => {
        this.showErrorModal(error, 'Unhandled Promise Rejection');
      }, 10);
      event.preventDefault(); // Prevent default browser error handling
    });

    logger.debug('Global error handler initialized');
  }

  /**
   * Show error modal with stack trace
   */
  private static showErrorModal(
    error: Error | any,
    message?: string,
    filename?: string,
    lineno?: number,
    colno?: number
  ): void {
    logger.debug('Showing error modal', { error: error?.message, filename, lineno });
    
    const errorMessage = error?.message || message || 'An unknown error occurred';
    const stack = error?.stack || 'No stack trace available';
    
    // Parse stack trace for better display
    const stackLines = stack.split('\n').map((line: string) => line.trim()).filter((line: string) => line.length > 0);
    
    // Build location info if available
    let locationInfo = '';
    if (filename) {
      locationInfo = `<p class="text-sm opacity-70">
        <strong>File:</strong> ${filename}${lineno ? `:${lineno}` : ''}${colno ? `:${colno}` : ''}
      </p>`;
    }

    const html = `
      <div class="space-y-4">
        <!-- Error Message -->
        <div class="alert alert-error">
          <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 class="font-bold">Unhandled Error</h3>
            <div class="text-sm">${this.escapeHtml(errorMessage)}</div>
          </div>
        </div>

        ${locationInfo}

        <!-- Stack Trace -->
        <div class="collapse collapse-arrow bg-base-200">
          <input type="checkbox" /> 
          <div class="collapse-title font-medium">
            <i class="fas fa-bug"></i> Stack Trace (click to expand)
          </div>
          <div class="collapse-content">
            <div class="mockup-code text-xs mt-2">
              ${stackLines.map((line: string, idx: number) => 
                `<pre data-prefix="${idx + 1}"><code>${this.escapeHtml(line)}</code></pre>`
              ).join('')}
            </div>
          </div>
        </div>

        <!-- Error Details -->
        <div class="collapse collapse-arrow bg-base-200">
          <input type="checkbox" /> 
          <div class="collapse-title font-medium">
            <i class="fas fa-info-circle"></i> Error Details
          </div>
          <div class="collapse-content">
            <div class="space-y-2 text-sm">
              <p><strong>Type:</strong> ${error?.name || 'Error'}</p>
              <p><strong>Message:</strong> ${this.escapeHtml(errorMessage)}</p>
              <p><strong>Time:</strong> ${new Date().toLocaleString()}</p>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="alert alert-warning">
          <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span>This error has been logged to the console. You may need to refresh the page.</span>
        </div>
      </div>
    `;

    logger.debug('About to call ModalSystem.show');
    
    try {
      const modalId = ModalSystem.show({
        title: '⚠️ Application Error',
        html,
        width: '3xl',
        buttons: [
          {
            caption: 'Copy Error',
            className: 'btn-ghost',
            callback: () => {
              this.copyToClipboard(`Error: ${errorMessage}\n\nStack Trace:\n${stack}`);
              ModalSystem.showMessage('Error details copied to clipboard!', 'Copied');
              return false; // Don't close the error modal
            }
          },
          {
            caption: 'Reload Page',
            className: 'btn-warning',
            callback: () => {
              window.location.reload();
            }
          },
          {
            caption: 'Close',
            primary: true
          }
        ]
      });
      
      logger.debug('ModalSystem.show returned modalId:', modalId);
    } catch (err) {
      // Fallback to console.error since modal system failed
      console.error('Error calling ModalSystem.show:', err);
      // Fallback: at least show an alert
      alert(`Application Error\n\n${errorMessage}\n\nCheck console for details.`);
    }
  }

  /**
   * Escape HTML to prevent XSS in error messages
   */
  private static escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Copy text to clipboard
   */
  private static copyToClipboard(text: string): void {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).catch(err => {
        logger.warn('Failed to copy to clipboard:', err);
      });
    } else {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
      } catch (err) {
        logger.warn('Failed to copy to clipboard:', err);
      }
      document.body.removeChild(textarea);
    }
  }

  /**
   * Manually trigger error display (useful for caught errors you want to show)
   */
  public static showError(error: Error | string, context?: string): void {
    const err = typeof error === 'string' ? new Error(error) : error;
    if (context) {
      logger.error(`Error in ${context}:`, err);
    }
    this.showErrorModal(err, context);
  }
}
